import DetachedDropdown, {
  detachedDropdownFallbackValue,
  DropdownValue,
} from '@/components/form/dropdown/DetachedDropdown';
import { AbsoluteNavLink } from '@/components/AbsoluteNavLink';
import classNames from 'classnames';
import React from 'react';
import { BffGamesCategory } from '@lucky7ventures/bff-types';
import { IntlShape, useIntl } from 'react-intl';
import { envIsCanada, envIsNormal, envIsSweden } from '@/constants/constants';
import { useSelector } from 'react-redux';
import { selectLocale } from '@/store/selectors/commonSelectors';
import { GTagEvents, triggerGTag } from '@/lib/gTagManager';
import { GIG_GAMES_CATEGORIES_RECORD } from '@/constants/games-constants';

const initialCategoryOption = (intl: IntlShape) => ({
  label: intl.formatMessage({ id: 'games.nav.categories' }),
  value: detachedDropdownFallbackValue,
});

function CategorySelector({
  categories,
  selectedCategory,
  onCategoryChange,
  onReset,
}: {
  categories: DropdownValue[];
  selectedCategory: BffGamesCategory | undefined;
  onCategoryChange: (category: string) => void;
  onReset: () => void;
}) {
  const intl = useIntl();
  const locale = useSelector(selectLocale);

  return (
    <>
      <div
        className={classNames('container:hidden', {
          'lg:hidden': envIsCanada() || envIsSweden() || (envIsNormal() && locale !== 'th'), // for specific situation we don't want the nav to collapse in the dropdown early
        })}
      >
        <DetachedDropdown
          data-cy="category-dropdown"
          label={intl.formatMessage({ id: 'games.nav.categories' })}
          options={categories}
          value={
            selectedCategory
              ? {
                  value: selectedCategory,
                  label: intl.formatMessage({ id: GIG_GAMES_CATEGORIES_RECORD[selectedCategory] }),
                }
              : initialCategoryOption(intl)
          }
          onChange={onCategoryChange}
        />
      </div>
      <div
        className={classNames(
          'hidden container:flex gap-2 [&>.active]:bg-blue-light [&>.active]:text-white',
          {
            'lg:flex': envIsCanada() || envIsSweden() || (envIsNormal() && locale !== 'th'), // for specific situation we don't want the nav to collapse in the dropdown early
          },
        )}
      >
        {categories.map(category => (
          <AbsoluteNavLink
            key={category.value + category.label}
            to={'/games/' + category.value}
            onClick={() => {
              triggerGTag(GTagEvents.games_nav_category, {
                slug: category.value,
              });
              onReset();
            }}
            className={classNames(
              'flex flex-shrink-0 gap-x-2 items-center cursor-pointer font-bold rounded-3xl py-2 px-4 bg-black/5 text-black/80 hover:bg-black/20',
            )}
          >
            {category.label}
          </AbsoluteNavLink>
        ))}
      </div>
    </>
  );
}

export default CategorySelector;
